import { buildAdjacency } from './rules.js'

// Pre-flight check before computing a route. Returns either:
//   { ok: true, route }  — route was found, ready to animate
//   { ok: false, code, message } — single user-facing error to display
export function validateRoute(graph, fromId, toId) {
  const { nodes = [], edges = [] } = graph || {}
  if (nodes.length === 0) {
    return { ok: false, code: 'NO_NODES', message: 'This floor has no nodes yet. Add nodes first.' }
  }
  if (!fromId) return { ok: false, code: 'NO_START', message: 'Pick a start anchor or node.' }
  if (!toId) return { ok: false, code: 'NO_DEST', message: 'Pick a destination.' }
  if (fromId === toId) {
    return { ok: false, code: 'SAME_POINT', message: 'Start and destination are the same point.' }
  }
  if (!nodes.find((n) => n.id === fromId)) {
    return { ok: false, code: 'START_MISSING', message: 'Start point references a node that no longer exists.' }
  }
  if (!nodes.find((n) => n.id === toId)) {
    return { ok: false, code: 'DEST_MISSING', message: 'Destination references a node that no longer exists.' }
  }
  if (edges.length === 0) {
    return {
      ok: false,
      code: 'NO_EDGES',
      message: 'There are no edges between nodes. Connect nodes with edges so a path can be drawn.',
    }
  }
  const route = shortestPath(graph, fromId, toId)
  if (!route) {
    return {
      ok: false,
      code: 'NO_PATH',
      message: 'No connected path between the start and destination. Add edges so they are linked.',
    }
  }
  return { ok: true, route }
}

export function shortestPath(graph, fromId, toId) {
  const { nodes = [], edges = [] } = graph || {}
  if (!fromId || !toId || fromId === toId) return null
  if (!nodes.find((n) => n.id === fromId) || !nodes.find((n) => n.id === toId)) return null

  const adjacency = buildAdjacency(nodes, edges)
  const edgeMap = new Map()
  for (const edge of edges) {
    const k1 = `${edge.from_node_id}->${edge.to_node_id}`
    edgeMap.set(k1, edge)
    if (edge.directionality !== 'one_way') {
      edgeMap.set(`${edge.to_node_id}->${edge.from_node_id}`, edge)
    }
  }

  const dist = new Map()
  const prev = new Map()
  const queue = new MinHeap()
  for (const node of nodes) dist.set(node.id, Infinity)
  dist.set(fromId, 0)
  queue.push({ id: fromId, d: 0 })

  while (!queue.empty()) {
    const { id, d } = queue.pop()
    if (d > dist.get(id)) continue
    if (id === toId) break
    const neighbors = adjacency.get(id) || []
    for (const neighbor of neighbors) {
      const edge = edgeMap.get(`${id}->${neighbor}`)
      if (!edge || edge.accessible === false) continue
      const weight = (edge.distance ?? 1) * (edge.penalty_weight || 1)
      const alt = d + weight
      if (alt < dist.get(neighbor)) {
        dist.set(neighbor, alt)
        prev.set(neighbor, id)
        queue.push({ id: neighbor, d: alt })
      }
    }
  }

  if (!Number.isFinite(dist.get(toId))) return null

  const path = []
  let current = toId
  while (current) {
    path.unshift(current)
    current = prev.get(current)
  }
  return { path, distance: dist.get(toId) }
}

class MinHeap {
  constructor() { this.data = [] }
  empty() { return this.data.length === 0 }
  push(item) {
    this.data.push(item)
    this.bubbleUp(this.data.length - 1)
  }
  pop() {
    const top = this.data[0]
    const last = this.data.pop()
    if (this.data.length > 0) {
      this.data[0] = last
      this.bubbleDown(0)
    }
    return top
  }
  bubbleUp(i) {
    while (i > 0) {
      const parent = (i - 1) >> 1
      if (this.data[parent].d <= this.data[i].d) break
      ;[this.data[parent], this.data[i]] = [this.data[i], this.data[parent]]
      i = parent
    }
  }
  bubbleDown(i) {
    const n = this.data.length
    for (;;) {
      const l = i * 2 + 1
      const r = l + 1
      let smallest = i
      if (l < n && this.data[l].d < this.data[smallest].d) smallest = l
      if (r < n && this.data[r].d < this.data[smallest].d) smallest = r
      if (smallest === i) break
      ;[this.data[smallest], this.data[i]] = [this.data[i], this.data[smallest]]
      i = smallest
    }
  }
}
